/**
 * The Initiative endpoints of the API surface table: list, detail, create,
 * update, validate, transitions, duplicate, delete, restore, Input_Requests,
 * change history, confidentiality, authorized viewers and warning
 * acknowledgements.
 *
 * ## What belongs in a schema and what does not
 *
 * These schemas describe the *wire shape*: which fields exist, their types, their
 * lengths and their closed value sets. Cross-field business rules do not live
 * here, and that separation is deliberate. R8.1 gives every validation rule a
 * stable identifier and R8.2 requires the unmet rule identifier to be reported
 * with the affected field; a schema has no rule identifier to report, so a
 * schema that rejected "end date before start date" would replace an `HV-xxx`
 * finding with an anonymous rejection and R5.7's validation summary would lose
 * the rule. The Validation_Engine is the single authority for those rules, and it
 * evaluates all four tiers in one pass (R8.12).
 *
 * The exception is {@link initiativeIntakeFormSchema}, which adds the purely
 * syntactic cross-field checks for the client-side immediate feedback design.md
 * calls for. The server does not use it: the server result stays authoritative.
 *
 * ## Unset text
 *
 * Every nullable text field parses through {@link nullableTextSchema}, so an
 * absent key, `null` and `''` all arrive as `null`. That is the `''`-to-`NULL`
 * ingest normalization, applied once at the boundary, and it is what makes the
 * `CHECK (col <> '')` constraints unreachable from a well-formed request and the
 * R14.6 unset-versus-empty export rule well defined.
 */
import { MAX_PLATFORMS } from '@complan/domain';
import { z } from 'zod';
import { assignmentSourceSchema, auditEventTypeSchema, dependencyFunctionSchema, fieldForceRoleSchema, initiativeActionSchema, initiativeFieldGroupSchema, initiativeStatusSchema, } from './enums.js';
import { findingSchema } from './errors.js';
// The Milestone_Readiness vocabulary, so the Detail_Panel's state and the dashboards'
// come from one enum (`complan-governance` R8.6).
import { readinessStateSchema } from './governance-enums.js';
import { initiativeFilterSchema, sortOrderSchema } from './filters.js';
import { COVERAGE_FACTOR_MAX, COVERAGE_FACTOR_MIN, DETAILS_MAX_LENGTH, FIELD_FORCE_SCOPE_MAX_ENTRIES, FREE_TEXT_MAX_LENGTH, FREQUENCY_MIN, HISTORY_PAGE_SIZE, IMPACTED_POPULATION_COUNT_MAX, IMPACTED_POPULATION_COUNT_MIN, INITIATIVE_PAGE_SIZE, INPUT_REQUEST_MAX_LENGTH, INPUT_REQUEST_MIN_LENGTH, MAX_REFERENCE_LINKS_PER_INITIATIVE, MIN_MANUAL_SALES_CYCLES_PER_YEAR, POPULATION_OVERRIDE_REASON_MAX_LENGTH, POPULATION_OVERRIDE_REASON_MIN_LENGTH, REFERENCE_LINK_LABEL_MAX_LENGTH, SHORT_TEXT_MAX_LENGTH, TITLE_MAX_LENGTH, TITLE_MIN_LENGTH, TRANSITION_COMMENT_MAX_LENGTH, } from './limits.js';
import { decimalTextSchema, idSchema, initiativeRefSchema, justificationSchema, nullableTextSchema, pagedResponseSchema, paginationQuerySchema, percentTextSchema, planningDateSchema, planningYearSchema, reasonSchema, ruleIdSchema, textSchema, timestampSchema, versionSchema, webAddressSchema, } from './primitives.js';
/** `/api/initiatives/:id`. */
export const initiativeIdParamsSchema = z.object({ id: idSchema });
/**
 * One member of the Platform set (R4.4, R4.5).
 *
 * `splitPercent` is `null` when no split was entered for this member and
 * canonical two-decimal text otherwise, never a double. Cardinality is bounded
 * here by `MAX_PLATFORMS` from the domain, so the limit has one definition; the
 * "exactly one primary" and "totals 100.00" rules belong to the Platform set
 * validator, which reports them with a rule identifier.
 */
export const initiativePlatformInputSchema = z.object({
    platformId: idSchema,
    isPrimary: z.boolean(),
    splitPercent: percentTextSchema.nullable().default(null),
});
/** A recorded milestone as submitted (R5.1, R12.2). */
export const initiativeMilestoneInputSchema = z.object({
    milestoneTypeId: idSchema,
    plannedDate: planningDateSchema,
    completedDate: planningDateSchema.nullable().default(null),
});
/** A DTE or CSC dependency as submitted (R5.5, R12.3). */
export const initiativeDependencyInputSchema = z.object({
    dependencyFunction: dependencyFunctionSchema,
    description: nullableTextSchema(FREE_TEXT_MAX_LENGTH),
    ownerText: nullableTextSchema(SHORT_TEXT_MAX_LENGTH),
    /** The activity date: a single date, or the start of a period. */
    dueDate: planningDateSchema.nullable().default(null),
    /**
     * The end of an activity period (migration 33); `null` for a single date. Needs
     * `dueDate` and must not precede it - see {@link refineDependencyPeriod}.
     */
    dueEndDate: planningDateSchema.nullable().default(null),
    /** `null` means open - the one definition the calendar filter and the panel share. */
    completedAt: timestampSchema.nullable().default(null),
});
/**
 * The activity-period rule migration 33's `initiative_dependency_due_end_*`
 * constraints enforce: an end date needs a start date and must not precede it.
 * Kept apart from the object schema so `.extend()` still works on it.
 */
function refineDependencyPeriod(schema) {
    return schema.superRefine((data, ctx) => {
        if (data.dueEndDate === null || data.dueEndDate === undefined)
            return;
        if (data.dueDate === null || data.dueDate === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['dueEndDate'],
                params: { key: 'dependency.activityPeriodRequiresStart' },
            });
            return;
        }
        if (data.dueEndDate < data.dueDate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['dueEndDate'],
                params: { key: 'dependency.activityPeriodOutOfOrder' },
            });
        }
    });
}
/**
 * A Field Force scope entry as submitted (R5.2, R12.9), extended by
 * `complan-manpower` Requirement 4 with the demand-input fields migration 19
 * adds to `initiative_ff_scope` directly (design.md: "Field_Force_Demand_Driver
 * is columns on `initiative_ff_scope`, not a new table").
 *
 * Role and audience level only. There is no employee field to submit, because
 * R18.13 forbids one and the column does not exist.
 *
 * ## The demand-input fields
 *
 * - `populationUnitId`, `posSegmentIds`, `impactedPopulationCount`,
 *   `coverageFactor`, `frequency`, `activityStartDate`/`activityEndDate` are
 *   nullable or default to "no demand input yet" (an empty array, `null`),
 *   matching migration 19's column shape: an existing scope entry created
 *   before this migration, or a new one created for governance tracking alone
 *   (Foundation R5.2) before its demand inputs are filled in, remains a valid
 *   submission with none of them set.
 * - `isPopulationOverride` is deliberately **absent** from this input schema.
 *   R4.3's override flag is decided by the service layer from request
 *   context - whether the submitted `impactedPopulationCount` replaces a
 *   POS_Segment-derived value already stored on the corresponding prior scope
 *   entry - which this schema, validating one submitted entry with no access
 *   to what was previously stored, cannot determine (design.md component 4;
 *   task 3.3's `deriveImpactedPopulationCount` helper computes the derived
 *   value, the service layer decides the flag). The read-back schema below
 *   carries it because a stored entry does have one.
 * - `populationOverrideReason` mirrors `ffs_override_reason_length` (10 to 500
 *   characters when present) directly, since a schema-level range check
 *   catches the common violation before the round trip that would otherwise
 *   raise it as that database constraint (design.md's rationale for task
 *   5.2's Effort_Assumption/Capacity_Period ranges, applied here too).
 * - The `superRefine` below mirrors two more CHECK constraints of migration
 *   19 directly, `ffs_activity_dates_pair` and `ffs_activity_dates_order`:
 *   both activity dates present or neither, and the end date not before the
 *   start date when both are present. These are schema-level mirrors of a
 *   database constraint, not Validation_Engine rules - they carry no `HV-xxx`
 *   identifier and are reported by field path and key like every other
 *   schema-level rejection in this package.
 */
/**
 * The plain-object half of {@link initiativeFieldForceScopeInputSchema}: every
 * field with no cross-field rule applied yet, kept separate so both the
 * submitted-entry schema (which adds the activity-dates `superRefine`) and the
 * stored-entry schema below (which extends this object with `id` and
 * `isPopulationOverride`) can build on the same field list with `.extend()` -
 * `ZodEffects` (what `.superRefine()` returns) has no `.extend()` of its own.
 */
const initiativeFieldForceScopeFieldsSchema = z.object({
    fieldForceRole: fieldForceRoleSchema,
    activityTypeId: idSchema,
    audienceId: idSchema.nullable().default(null),
    hasMaterial: z.boolean().default(false),
    workOrder: nullableTextSchema(SHORT_TEXT_MAX_LENGTH),
    informationContribution: nullableTextSchema(FREE_TEXT_MAX_LENGTH),
    /** `population_unit_id`: `null` when no demand input has been entered yet. */
    populationUnitId: idSchema.nullable().default(null),
    /**
     * `region_id` (migration 23): the Region this driver's demand belongs to,
     * the dimension Requirement 6.2 divides Demand_Hours by Capacity_Hours
     * within and Requirement 6.5 drills through by. `null` for a driver whose
     * Region is not recorded - it still counts toward FF-role and
     * Initiative-level totals, and is excluded from Region-scoped Utilization
     * rather than attributed to a guessed Region.
     */
    regionId: idSchema.nullable().default(null),
    /** `pos_segment_ids`: empty means a direct manual entry, non-empty means POS_Segment-derived (R4.1, R4.2). */
    posSegmentIds: z.array(idSchema).default([]),
    impactedPopulationCount: decimalTextSchema({
        min: IMPACTED_POPULATION_COUNT_MIN,
        max: IMPACTED_POPULATION_COUNT_MAX,
        decimalPlaces: 2,
    })
        .nullable()
        .default(null),
    /** 10 to 500 characters when present (R4.3), required by the service layer exactly when it decides this entry is an override. */
    populationOverrideReason: nullableTextSchema(POPULATION_OVERRIDE_REASON_MAX_LENGTH).refine((value) => value === null || value.length >= POPULATION_OVERRIDE_REASON_MIN_LENGTH, { params: { key: 'populationOverrideReason.tooShort' } }),
    /** 0.00 to 100.00; Assumption A15 permits 0.00 once a value is entered (R4.4). */
    coverageFactor: decimalTextSchema({
        min: COVERAGE_FACTOR_MIN,
        max: COVERAGE_FACTOR_MAX,
        decimalPlaces: 2,
    })
        .nullable()
        .default(null),
    /** A positive integer once entered (R4.4). */
    frequency: z.number().int().min(FREQUENCY_MIN).nullable().default(null),
    activityStartDate: planningDateSchema.nullable().default(null),
    activityEndDate: planningDateSchema.nullable().default(null),
});
/** The activity-dates cross-field rule migration 19's `ffs_activity_dates_pair`/`ffs_activity_dates_order` constraints enforce, applied to any object carrying both fields. */
function refineActivityDates(schema) {
    return schema.superRefine((data, ctx) => {
        const hasStart = data.activityStartDate !== null;
        const hasEnd = data.activityEndDate !== null;
        if (hasStart !== hasEnd) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['activityEndDate'],
                params: { key: 'fieldForceScope.activityDatesRequirePair' },
            });
            return;
        }
        if (hasStart &&
            hasEnd &&
            data.activityEndDate < data.activityStartDate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['activityEndDate'],
                params: { key: 'fieldForceScope.activityDatesOutOfOrder' },
            });
        }
    });
}
/**
 * A Field Force scope entry as submitted (R5.2, R12.9), extended by
 * `complan-manpower` Requirement 4 with the demand-input fields migration 19
 * adds to `initiative_ff_scope` directly (design.md: "Field_Force_Demand_Driver
 * is columns on `initiative_ff_scope`, not a new table").
 *
 * Role and audience level only. There is no employee field to submit, because
 * R18.13 forbids one and the column does not exist.
 *
 * ## The demand-input fields
 *
 * - `populationUnitId`, `posSegmentIds`, `impactedPopulationCount`,
 *   `coverageFactor`, `frequency`, `activityStartDate`/`activityEndDate` are
 *   nullable or default to "no demand input yet" (an empty array, `null`),
 *   matching migration 19's column shape: an existing scope entry created
 *   before this migration, or a new one created for governance tracking alone
 *   (Foundation R5.2) before its demand inputs are filled in, remains a valid
 *   submission with none of them set.
 * - `isPopulationOverride` is deliberately **absent** from this input schema.
 *   R4.3's override flag is decided by the service layer from request
 *   context - whether the submitted `impactedPopulationCount` replaces a
 *   POS_Segment-derived value already stored on the corresponding prior scope
 *   entry - which this schema, validating one submitted entry with no access
 *   to what was previously stored, cannot determine (design.md component 4;
 *   task 3.3's `deriveImpactedPopulationCount` helper computes the derived
 *   value, the service layer decides the flag). The read-back schema below
 *   carries it because a stored entry does have one.
 * - `populationOverrideReason` mirrors `ffs_override_reason_length` (10 to 500
 *   characters when present) directly, since a schema-level range check
 *   catches the common violation before the round trip that would otherwise
 *   raise it as that database constraint (design.md's rationale for task
 *   5.2's Effort_Assumption/Capacity_Period ranges, applied here too).
 * - The `superRefine` applied by {@link refineActivityDates} mirrors two more
 *   CHECK constraints of migration 19 directly, `ffs_activity_dates_pair` and
 *   `ffs_activity_dates_order`: both activity dates present or neither, and
 *   the end date not before the start date when both are present. These are
 *   schema-level mirrors of a database constraint, not Validation_Engine
 *   rules - they carry no `HV-xxx` identifier and are reported by field path
 *   and key like every other schema-level rejection in this package.
 */
export const initiativeFieldForceScopeInputSchema = refineActivityDates(initiativeFieldForceScopeFieldsSchema);
/** A reference link: `http` or `https` only, no uploaded files in Foundation (R12.4, R12.9). */
export const referenceLinkInputSchema = z.object({
    label: nullableTextSchema(REFERENCE_LINK_LABEL_MAX_LENGTH),
    url: webAddressSchema,
});
/**
 * The assigned Sales_Cycle set and how it was arrived at (R7.3, R7.4).
 *
 * Discriminated on the source so a manual override cannot be submitted without
 * the 10-to-500-character reason R7.4 requires, and a derived assignment cannot
 * carry a reason that would never be shown. The per-year cardinality of R7.4 (1
 * to 12 active cycles per intersected year) needs the Initiative date range and
 * the cycle definitions, so it is a domain rule, not a schema one.
 */
export const salesCycleAssignmentInputSchema = z.discriminatedUnion('source', [
    z.object({
        source: z.literal('derived'),
        salesCycleIds: z.array(idSchema).default([]),
    }),
    z.object({
        source: z.literal('manual'),
        salesCycleIds: z.array(idSchema).min(MIN_MANUAL_SALES_CYCLES_PER_YEAR),
        reason: justificationSchema,
    }),
]);
/**
 * The Initiative_Intake_Form payload: every stored field of R4.2 and every
 * association of R4.3.
 *
 * Association arrays carry no upper bound. R4.3 sets a floor - at least 20
 * entries per association type - not a ceiling, and inventing one here would
 * reject data the requirement permits; request size is bounded by the server's
 * body limit instead.
 */
export const initiativeWriteSchema = z.object({
    title: textSchema(TITLE_MIN_LENGTH, TITLE_MAX_LENGTH),
    details: nullableTextSchema(DETAILS_MAX_LENGTH),
    ownerUserId: idSchema,
    /** R4.10 requires it to differ from the Owner; that comparison is a domain rule. */
    backupOwnerUserId: idSchema.nullable().default(null),
    startDate: planningDateSchema,
    endDate: planningDateSchema,
    platforms: z.array(initiativePlatformInputSchema).min(1).max(MAX_PLATFORMS),
    channelIds: z.array(idSchema).default([]),
    customerGroupIds: z.array(idSchema).default([]),
    audienceIds: z.array(idSchema).default([]),
    categoryIds: z.array(idSchema).default([]),
    initiativeTypeIds: z.array(idSchema).default([]),
    salesCycleAssignment: salesCycleAssignmentInputSchema,
    priorityId: idSchema,
    isConfidential: z.boolean().default(false),
    /** Required by R3.10 when the confidentiality attribute changes; the service decides when. */
    confidentialityReason: justificationSchema.nullable().default(null),
    fieldForceInvolvement: z.boolean().default(false),
    dteInvolvement: z.boolean().default(false),
    cscInvolvement: z.boolean().default(false),
    /** CSC attribute master-data ids (Inbound, Outbound, ...); meaningful only while CSC is involved. */
    cscAttributeIds: z.array(idSchema).default([]),
    milestones: z.array(initiativeMilestoneInputSchema).default([]),
    dependencies: z.array(refineDependencyPeriod(initiativeDependencyInputSchema)).default([]),
    fieldForceScope: z
        .array(initiativeFieldForceScopeInputSchema)
        .max(FIELD_FORCE_SCOPE_MAX_ENTRIES)
        .superRefine((entries, ctx) => {
        // One activity type at most once per role: a repeat would count the same
        // activity's demand twice.
        const seen = new Set();
        entries.forEach((entry, index) => {
            if (entry.activityTypeId === '')
                return;
            const key = `${entry.fieldForceRole}:${entry.activityTypeId}`;
            if (seen.has(key)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [index, 'activityTypeId'],
                    params: { key: 'fieldForceScope.duplicateActivityType' },
                });
            }
            seen.add(key);
        });
    })
        .default([]),
    referenceLinks: z
        .array(referenceLinkInputSchema)
        .max(MAX_REFERENCE_LINKS_PER_INITIATIVE)
        .default([]),
});
/**
 * The client-side resolver schema: the write shape plus the syntactic cross-field
 * checks design.md wants evaluated in the form for immediate feedback (date
 * order, R8.3).
 *
 * Not used by the API. The server evaluates the same condition through the
 * Validation_Engine, which reports it as a hard finding carrying its rule
 * identifier, and that result remains authoritative (R5.7).
 */
export const initiativeIntakeFormSchema = initiativeWriteSchema.superRefine((value, ctx) => {
    if (value.endDate < value.startDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['endDate'],
            params: { key: 'date_range.end_before_start' },
        });
    }
});
/**
 * `POST /api/initiatives`. Creates with status Draft (R9.1), or with the status
 * the user selected where the Authorization_Service and the Validation_Engine
 * permit that transition (R5.6).
 */
export const createInitiativeRequestSchema = initiativeWriteSchema.extend({
    requestedStatus: initiativeStatusSchema.nullable().default(null),
});
/**
 * `PUT /api/initiatives/:id`. Carries the loaded record version; a mismatch is a
 * `409` with a per-field diff and no automatic retry (R4.9, R5.8).
 */
export const updateInitiativeRequestSchema = initiativeWriteSchema.extend({
    version: versionSchema,
});
/**
 * `POST /api/initiatives/:id/validate`. Findings for the live form, nothing
 * persisted (R5.7).
 *
 * The payload is the write shape with every field optional, because a form under
 * edit is incomplete by definition and the point of the call is to be told so.
 */
export const validateInitiativeRequestSchema = z.object({
    version: versionSchema.nullable().default(null),
    initiative: initiativeWriteSchema.partial(),
});
/**
 * The complete finding set of one evaluation - every active rule of all four
 * tiers, never a prefix (R8.12).
 */
export const validateInitiativeResponseSchema = z.object({
    findings: z.array(findingSchema).default([]),
    /** Warnings of this evaluation that still need an acknowledgement (R8.5, R8.11). */
    unacknowledgedWarningRuleIds: z.array(ruleIdSchema).default([]),
});
/** A stored Platform association as read back. */
export const initiativePlatformSchema = initiativePlatformInputSchema;
/** A stored milestone as read back, ordered by ascending planned date (R12.2). */
export const initiativeMilestoneSchema = initiativeMilestoneInputSchema.extend({
    id: idSchema,
    /**
     * The display name of the referenced milestone type, resolved server-side so
     * the Detail_Panel never shows a raw id. `null` when the referenced type row no
     * longer exists (a hard-deleted or otherwise missing master data record); the
     * client falls back to the id in that case.
     */
    milestoneTypeName: z.string().nullable(),
    /**
     * Whether this milestone's type carries the mandatory rule flag
     * (`complan-governance` R8.1, R8.10).
     *
     * A non-mandatory milestone is shown in the Detail_Panel and excluded from every
     * readiness figure, so the panel is told which it is rather than inferring it from an
     * absent state - "not required" and "not classified" are different facts.
     */
    isRequired: z.boolean().default(false),
    /**
     * The Milestone_Readiness state of R8.3, or null for a milestone that is not required.
     *
     * R8.6 makes this the single definition of Overdue, shared by the Detail_Panel, both
     * dashboards, the R6.12 filter dimension and every export. The Detail_Panel used to
     * derive it from the browser clock, which knew nothing of the Readiness_Horizon and
     * could therefore disagree with the dashboard about the same milestone.
     */
    readinessState: readinessStateSchema.nullable().default(null),
});
/**
 * A stored dependency as read back, with the open state resolved (R12.3).
 *
 * `isOpen` is derived from `completedAt` by the one predicate the Calendar_View
 * open-dependency filter also evaluates, so the panel and the filter cannot
 * disagree.
 */
export const initiativeDependencySchema = initiativeDependencyInputSchema.extend({
    id: idSchema,
    isOpen: z.boolean(),
    /**
     * The display name for `ownerText` when it holds a known user id, resolved
     * server-side so the Detail_Panel shows a name rather than a raw id. The
     * dependency owner is chosen from the user directory on the intake form, so
     * `ownerText` is normally a user id; this is that id resolved. `null` when the
     * value is not a known user id (a since-deleted account, or a free-text value),
     * in which case the client falls back to `ownerText`.
     */
    ownerDisplayName: z.string().nullable(),
});
/**
 * A stored Field Force scope entry as read back, additionally carrying the
 * identifier, R4.3's override flag and the two values R4.2/R4.3 require to be
 * shown *beside* a population count rather than instead of it.
 *
 * - `isPopulationOverride` is set by the service layer (never inferred here from
 *   the presence of a reason alone) and is therefore present only once a row
 *   exists to carry it - see {@link initiativeFieldForceScopeInputSchema}'s note
 *   on why the submitted-entry schema does not carry it.
 * - `derivedPopulationCount` is the POS_Segment-derived count for the stored
 *   selection, re-derived on every read from `posSegmentIds` and the current
 *   `pos_segment` counts rather than stored: R4.3's "SHALL retain and display
 *   the POS_Segment-derived count alongside the override rather than replacing
 *   it" is satisfied by retaining the *selection*, which is what the count is
 *   derived from. `null` for a direct entry, which carries no selection (R4.1).
 * - `populationSourceTimestamp` is R4.2's "SHALL display that source timestamp
 *   alongside the derived count": the oldest `source_timestamp` among the
 *   selected segments, since a derived count is only as current as its least
 *   recently sourced contributor. `null` when no selected segment has been
 *   sourced yet (Assumption A8).
 *
 * All three are read-only projections: none appears on the input schema, and a
 * submitted value for any of them is not accepted anywhere.
 */
export const initiativeFieldForceScopeSchema = refineActivityDates(initiativeFieldForceScopeFieldsSchema.extend({
    id: idSchema,
    isPopulationOverride: z.boolean().default(false),
    // `numeric(12,2)` decimal text, validated as plain text rather than through
    // `decimalTextSchema`: this is a server-computed projection of master data
    // the client never submits, so a range and decimal-place check here would
    // re-validate the server's own arithmetic while adding to an already deep
    // inference chain on the Detail_Panel response.
    derivedPopulationCount: z.string().nullable().default(null),
    populationSourceTimestamp: z.string().nullable().default(null),
}));
/** A stored reference link as read back. */
export const referenceLinkSchema = referenceLinkInputSchema.extend({ id: idSchema });
/**
 * The assigned Sales_Cycle set as read back (R7.6).
 *
 * Both sets are present: while an override is in force the manual set is the
 * effective assignment and the derived set is recomputed for display only (R7.7),
 * so the panel can show the divergence R7.8 warns about.
 */
export const salesCycleAssignmentSchema = z.object({
    source: assignmentSourceSchema,
    assignedSalesCycleIds: z.array(idSchema).default([]),
    derivedSalesCycleIds: z.array(idSchema).default([]),
    overrideReason: nullableTextSchema(500),
    overrideByUserId: idSchema.nullable().default(null),
    /** The display name behind `overrideByUserId`, resolved server-side; `null` when there is no override. */
    overrideByDisplayName: z.string().nullable().default(null),
    overrideAt: timestampSchema.nullable().default(null),
});
/**
 * `GET /api/initiatives/:id`. The Detail_Panel payload (R12.1).
 *
 * Field-level projection is expressed by narrowing this schema at the route with
 * `.pick()`, not by nulling a value: R12.1 requires a withheld field to be absent
 * with no placeholder, label or marker, and Fastify's schema-based serialization
 * makes absence enforceable rather than aspirational. Foundation's withholding is
 * record-level - RLS returns no row - so the narrowed variants exist for the
 * routes that need them rather than for every field.
 */
export const initiativeDetailResponseSchema = z.object({
    id: idSchema,
    initiativeRef: initiativeRefSchema,
    title: z.string(),
    details: z.string().nullable(),
    ownerUserId: idSchema,
    backupOwnerUserId: idSchema.nullable(),
    /**
     * The Owner's display name, resolved server-side from the Identity_Service
     * projection. Falls back to `ownerUserId` when the account row is gone (a
     * deleted account, R4.11's "stored assignment stays"), so the panel always
     * has *something* to show rather than an empty label.
     */
    ownerDisplayName: z.string().min(1),
    backupOwnerDisplayName: z.string().min(1).nullable(),
    /** R4.11: a stored Owner the Identity_Service now reports inactive stays stored. */
    ownerAccountActive: z.boolean(),
    backupOwnerAccountActive: z.boolean().nullable(),
    startDate: planningDateSchema,
    endDate: planningDateSchema,
    /** Derived from the start date and not directly settable (R4.8). */
    planningYear: planningYearSchema,
    /** Every calendar year the date range intersects (R4.12). */
    visibleYears: z.array(planningYearSchema),
    platforms: z.array(initiativePlatformSchema),
    channelIds: z.array(idSchema),
    customerGroupIds: z.array(idSchema),
    audienceIds: z.array(idSchema),
    categoryIds: z.array(idSchema),
    initiativeTypeIds: z.array(idSchema),
    salesCycleAssignment: salesCycleAssignmentSchema,
    status: initiativeStatusSchema,
    priorityId: idSchema,
    isConfidential: z.boolean(),
    /** Present only for a user permitted to maintain it (`initiative:confidentiality`). */
    authorizedViewerUserIds: z.array(idSchema).default([]),
    fieldForceInvolvement: z.boolean(),
    dteInvolvement: z.boolean(),
    cscInvolvement: z.boolean(),
    cscAttributeIds: z.array(idSchema).default([]),
    milestones: z.array(initiativeMilestoneSchema),
    dependencies: z.array(initiativeDependencySchema),
    fieldForceScope: z.array(initiativeFieldForceScopeSchema),
    referenceLinks: z.array(referenceLinkSchema),
    cancellationReason: z.string().nullable(),
    /** R9.4, R12.10: the flag with the actor and time of the most recent material change. */
    changedAfterApproval: z.boolean(),
    changedAfterApprovalByUserId: idSchema.nullable(),
    /** The display name behind `changedAfterApprovalByUserId`; `null` when the flag is not set. */
    changedAfterApprovalByDisplayName: z.string().nullable(),
    changedAfterApprovalAt: timestampSchema.nullable(),
    recordSource: z.string().min(1),
    version: versionSchema,
    createdByUserId: idSchema,
    /** The display name behind `createdByUserId`, resolved server-side. */
    createdByDisplayName: z.string().min(1),
    createdAt: timestampSchema,
    modifiedByUserId: idSchema,
    /** The display name behind `modifiedByUserId`, resolved server-side. */
    modifiedByDisplayName: z.string().min(1),
    modifiedAt: timestampSchema,
    /** Capability-gated (R12.7, R12.8): a Read_Only_User receives `['input_request']`. */
    availableActions: z.array(initiativeActionSchema).default([]),
});
/** The identifier and version a write answers with. */
export const initiativeWriteResponseSchema = z.object({
    id: idSchema,
    initiativeRef: initiativeRefSchema,
    status: initiativeStatusSchema,
    version: versionSchema,
});
/**
 * `GET /api/initiatives`. One row of the paginated list, carrying the fields the
 * filtered-view export names (R15.1) so the list and its export read one shape.
 */
export const initiativeListItemSchema = z.object({
    id: idSchema,
    initiativeRef: initiativeRefSchema,
    title: z.string(),
    ownerUserId: idSchema,
    backupOwnerUserId: idSchema.nullable(),
    startDate: planningDateSchema,
    endDate: planningDateSchema,
    planningYear: planningYearSchema,
    salesCycleIds: z.array(idSchema),
    primaryPlatformId: idSchema,
    additionalPlatformIds: z.array(idSchema),
    channelIds: z.array(idSchema),
    audienceIds: z.array(idSchema),
    categoryIds: z.array(idSchema),
    initiativeTypeIds: z.array(idSchema),
    status: initiativeStatusSchema,
    priorityId: idSchema,
    isConfidential: z.boolean(),
    fieldForceInvolvement: z.boolean(),
    /**
     * The distinct Field Force roles the Initiative's scope entries name, ascending
     * and without repetition, so the list can render the role tags of UI spec 3.4
     * without a second request per row.
     *
     * Role level only, never an employee: `initiative_ff_scope` holds no
     * employee-identifying column by construction (R18.13), and this is the same
     * dimension `fieldForceRoles` filters on (R11.5). Empty for an Initiative with
     * no Field Force involvement, which is a real answer rather than a gap - it is
     * what `fieldForceInvolvement: false` means in row form.
     */
    fieldForceRoles: z.array(fieldForceRoleSchema),
    dteInvolvement: z.boolean(),
    cscInvolvement: z.boolean(),
    changedAfterApproval: z.boolean(),
    version: versionSchema,
});
/**
 * Lifts the flat `sortField` / `sortDirection` query parameters into the nested
 * `sort` object this request carries.
 *
 * A URL query string has no nesting. Fastify's default parser produces flat keys,
 * so `?sort[field]=title` arrives as a key literally named `sort[field]`, never
 * reaches `sort`, and `sortOrderSchema`'s own default then quietly supplies
 * `start_date` ascending - which made the sort order of this endpoint
 * unselectable over HTTP, silently rather than as an error. Accepting the flat
 * spelling is what makes it reachable, and it is the same spelling the calendar
 * screen already writes into its own URL.
 *
 * A caller that already holds a `sort` object passes it through untouched, so
 * constructing the request programmatically still works.
 */
function liftSortOrder(value) {
    if (value === null || typeof value !== 'object') {
        return value;
    }
    const record = value;
    if (record['sort'] !== undefined) {
        return record;
    }
    const field = record['sortField'];
    const direction = record['sortDirection'];
    if (field === undefined && direction === undefined) {
        return record;
    }
    return {
        ...record,
        sort: {
            ...(field !== undefined ? { field } : {}),
            ...(direction !== undefined ? { direction } : {}),
        },
    };
}
/**
 * `GET /api/initiatives`: the shared filter grammar, a sort order and a page
 * window.
 *
 * The sort order is accepted as `sortField` / `sortDirection`; see
 * {@link liftSortOrder} for why the nested spelling cannot survive a query string.
 */
export const initiativeListQuerySchema = z.preprocess(liftSortOrder, initiativeFilterSchema
    .extend({ sort: sortOrderSchema })
    .merge(paginationQuerySchema(INITIATIVE_PAGE_SIZE)));
/**
 * A page of Initiatives with the count of distinct identifiers the requesting
 * user may read (R11.9, R13.8).
 */
export const initiativeListResponseSchema = pagedResponseSchema(initiativeListItemSchema);
/**
 * `POST /api/initiatives/:id/transitions` (R9.2, R9.12, R9.13).
 *
 * Both text fields are present because R9 uses them for different things: the
 * reason is required by the transition table for the reason-carrying transitions
 * and for a cancellation (1 to 500 characters, R9.6, R9.12), while the comment is
 * always optional and holds up to 1,000 characters (R9.2). Which one a given
 * transition demands is the transition table's answer, not the schema's.
 */
export const transitionRequestSchema = z.object({
    targetStatus: initiativeStatusSchema,
    version: versionSchema,
    reason: reasonSchema.nullable().default(null),
    comment: nullableTextSchema(TRANSITION_COMMENT_MAX_LENGTH),
});
/** The new status and version after a permitted transition. */
export const transitionResponseSchema = z.object({
    id: idSchema,
    status: initiativeStatusSchema,
    version: versionSchema,
    transitionedAt: timestampSchema,
});
/**
 * `POST /api/initiatives/:id/duplicate` (R9.8). The copied field groups are the
 * Initiative_Intake_Form sections of R5.1.
 *
 * The Explicit_Authorization_List is not a field group: R9.8 names the
 * confidentiality attribute only, and copying an authorization list would widen
 * access silently.
 */
export const duplicateInitiativeRequestSchema = z.object({
    fieldGroups: z.array(initiativeFieldGroupSchema).min(1),
});
/** `DELETE /api/initiatives/:id`. Soft delete, with the retention deadline (R9.9). */
export const deleteInitiativeResponseSchema = z.object({
    id: idSchema,
    deletedAt: timestampSchema,
    /** Measured from the deletion timestamp; the window itself is configuration (R9.9). */
    contentRetainedUntil: timestampSchema,
});
/**
 * `POST /api/initiatives/:id/restore` (R9.10). Restores the status held
 * immediately before the deletion; a request after the retention window is a
 * rejection, not a restore (R9.11).
 */
export const restoreInitiativeResponseSchema = z.object({
    id: idSchema,
    status: initiativeStatusSchema,
    version: versionSchema,
    restoredAt: timestampSchema,
});
/**
 * `POST /api/initiatives/:id/input-requests` (R2.6, R2.11). Text of 1 to 2,000
 * characters.
 *
 * It carries no `version`, and that absence is the contract: an Input_Request
 * leaves every field value, the record version and the modified-at value
 * unchanged, so there is no version to check and none to bump.
 */
export const createInputRequestSchema = z.object({
    text: textSchema(INPUT_REQUEST_MIN_LENGTH, INPUT_REQUEST_MAX_LENGTH),
});
/** One Input_Request as read back, newest first, 25 per page (R12.6). */
export const inputRequestSchema = z.object({
    id: idSchema,
    initiativeId: idSchema,
    actorUserId: idSchema,
    /** The submitting user's display name, resolved server-side from `actorUserId`. */
    actorDisplayName: z.string().min(1),
    submittedAt: timestampSchema,
    text: z.string().min(INPUT_REQUEST_MIN_LENGTH).max(INPUT_REQUEST_MAX_LENGTH),
});
/** A page of Input_Requests (R12.6). */
export const inputRequestListResponseSchema = pagedResponseSchema(inputRequestSchema);
/**
 * One Change_Event of the change history (R12.5, R17.1-R17.5).
 *
 * The history is permission-filtered per row: a user sees only the events whose
 * object, field and values they may read, with no count and no gap marker for the
 * rest (R17.5). That is why the page carries a total of readable events only.
 */
export const changeEventSchema = z.object({
    id: z.string().min(1),
    occurredAt: timestampSchema,
    eventType: auditEventTypeSchema,
    actorUserId: idSchema.nullable(),
    /** The actor's display name, resolved server-side; `null` for a system-originated event with no actor. */
    actorDisplayName: z.string().nullable(),
    objectType: z.string().min(1),
    objectId: idSchema.nullable(),
    fieldPath: z.string().nullable(),
    previousValue: z.unknown(),
    newValue: z.unknown(),
    reason: z.string().nullable(),
    /** The record version at the time of the change (R17.4). */
    recordVersion: versionSchema.nullable(),
    /** Set for a warning acknowledgement (R8.8). */
    ruleId: ruleIdSchema.nullable(),
});
/** `GET /api/initiatives/:id/history`. Paged at 25, reverse chronological (R12.5). */
export const changeHistoryQuerySchema = paginationQuerySchema(HISTORY_PAGE_SIZE);
/** A page of Change_Events (R12.5, R17.5). */
export const changeHistoryResponseSchema = pagedResponseSchema(changeEventSchema);
/**
 * `PUT /api/initiatives/:id/confidentiality` (R3.8, R3.10).
 *
 * The reason is required, not nullable: R3.10 blocks the change without one, and
 * a schema that accepted `null` would move that rule to the service for no gain.
 */
export const confidentialityRequestSchema = z.object({
    isConfidential: z.boolean(),
    reason: justificationSchema,
    version: versionSchema,
});
/**
 * `PUT /api/initiatives/:id/authorized-viewers` (R3.9, R3.11). A list replace, so
 * the audit entry can record the previous and the new list in one event.
 */
export const authorizedViewersRequestSchema = z.object({
    userIds: z.array(idSchema),
    reason: justificationSchema,
    version: versionSchema,
});
/** The state a confidentiality or viewer-list change leaves behind. */
export const confidentialityResponseSchema = z.object({
    id: idSchema,
    isConfidential: z.boolean(),
    authorizedViewerUserIds: z.array(idSchema),
    version: versionSchema,
});
/**
 * One warning acknowledgement: the rule and the witness hash the acknowledgement
 * is bound to (R8.5, R8.8, R8.11).
 *
 * The hash is what makes expiry a consequence of the data: a later edit that
 * changes the acknowledged values changes the witness, and the stored
 * acknowledgement no longer matches.
 */
export const warningAcknowledgementSchema = z.object({
    ruleId: ruleIdSchema,
    witnessHash: z.string().min(1).max(128),
});
/**
 * `POST /api/initiatives/:id/warning-acknowledgements`. Every warning raised by
 * the request being completed, acknowledged in one call (R8.5).
 */
export const warningAcknowledgementRequestSchema = z.object({
    acknowledgements: z.array(warningAcknowledgementSchema).min(1),
});
/** The acknowledgements now on record for the Initiative. */
export const warningAcknowledgementResponseSchema = z.object({
    id: idSchema,
    acknowledged: z.array(warningAcknowledgementSchema),
});
//# sourceMappingURL=initiative.js.map